<template>
	<div class="relative rounded-lg bg-white shadow" :class="classes" data-testid="panel">
		<div
			v-if="title"
			class="px-4 py-3 sm:py-4 sm:px-6 flex justify-between items-center"
			:class="collapsible ? 'hover:cursor-pointer' : ''"
			@click="collapsible ? (open = !open) : null">
			<span class="font-semibold text-sm sm:text-md" data-testid="panel-title">{{
				title
			}}</span>

			<div class="flex items-center gap-4">
				<div id="panel-header-portal"></div>

				<div v-if="$slots.header">
					<slot name="header"></slot>
				</div>

				<app-tooltip v-if="help" :text="help" position-y="bottom" position-x="right">
					<font-awesome-icon
						icon="fa-solid fa-circle-info"
						class="text-mabey-600"></font-awesome-icon>
				</app-tooltip>

				<font-awesome-icon
					v-if="collapsible"
					icon="fa-solid fa-angle-down"
					class="text-mabey-600"
					:class="open ? 'rotate-180' : ''"
					data-testid="caret"></font-awesome-icon>
			</div>
		</div>

		<div ref="body" :class="bodyClasses" data-testid="panel-body">
			<div :class="contentClasses">
				<text-skeleton v-if="loading && !loadingOverlay" />
				<slot v-else></slot>
			</div>
		</div>

		<div
			v-if="loading && loadingOverlay"
			class="flex items-center justify-center w-full h-full absolute top-0 bottom-0 bg-white/75 z-30">
			<app-loader size="large" />
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { throttle } from 'lodash'
	import { ref, computed, onMounted, watch } from 'vue'

	const props = withDefaults(
		defineProps<{
			title?: string
			help?: string
			compact?: boolean
			collapsible?: boolean
			loading?: boolean
			loadingOverlay?: boolean
			scrollListener?: boolean
		}>(),
		{
			title: undefined,
			help: undefined,
			compact: false,
			collapsible: false,
			loading: false,
			scrollListener: false,
			loadingOverlay: false
		}
	)

	const body = ref()
	const collapsing = ref(false)
	const open = ref(false)

	const classes = computed(() => ({
		'divide-y divide-gray-200': props.title,
		'flex flex-col': props.scrollListener
	}))

	const bodyClasses = computed(() => ({
		relative: true,
		'overflow-y-scroll h-full': props.scrollListener,
		'transition-all duration-500': props.collapsible,
		'max-h-0 py-0 overflow-hidden': props.collapsible && !open.value,
		'max-h-[1000px]': props.collapsible && collapsing.value
	}))

	const contentClasses = computed(() => ({
		relative: true,
		'px-4 sm:p-6': !props.compact,
		'py-5': !props.compact && ((props.collapsible && open.value) || !props.collapsible)
	}))

	const scrolling = throttle(() => {
		const el = body.value
		const offset = 5
		const scrollOffset = el.scrollHeight - (el.scrollTop + offset)
		const scrolled = scrollOffset <= el.clientHeight

		if (scrolled) {
			emit('scrolled')
		}
	}, 500)

	const emit = defineEmits(['scrolled'])

	watch(
		() => open.value,
		(newVal) => {
			if (newVal == true) {
				collapsing.value = true

				setTimeout(() => {
					collapsing.value = false
				}, 300)
			}
		}
	)

	onMounted(() => {
		if (props.scrollListener) {
			body.value.addEventListener('scroll', scrolling)
		}
	})
</script>
